import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PREDICTION_FIELDS, todayISO } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trophy, CalendarDays, Sparkles, RefreshCw, CheckCircle2, PencilLine, AlertTriangle, Trash2 } from "lucide-react";
import LiveScoreStatusBadge from "@/components/admin/LiveScoreStatusBadge";

function ResultRow({ match, onChanged }) {
  const initial = match.actual || {};
  const fetched = match.fetched_stats || null;
  // If editor hasn't finalised (no actual yet) but we have an auto-fetched
  // snapshot, prefill the inputs with the fetched values.
  const prefill = (key) => {
    if (initial[key] != null) return String(initial[key]);
    if (fetched && fetched[key] != null) return String(fetched[key]);
    return "";
  };
  const [vals, setVals] = useState({
    goals: prefill("goals"),
    shots_on_target: prefill("shots_on_target"),
    corners: prefill("corners"),
    offsides: prefill("offsides"),
    throw_ins: prefill("throw_ins"),
    fouls: prefill("fouls"),
  });
  const [saving, setSaving] = useState(false);
  const [refetching, setRefetching] = useState(false);
  const isFinished = match.status === "finished";
  const isAutoFetched = !!fetched && !isFinished;
  // "Awaiting result" = match has kicked off but no actual stats saved yet.
  // (We approximate the finished window with the `kickoff_passed` flag from
  // the backend; the matches/pending-results endpoint already filters by
  // kickoff + 120 minutes (≈ 15 min after the final whistle), so anything in
  // that list is over.)
  const isAwaitingResult = !isFinished && !match.actual && match.kickoff_passed;
  // Match is "finished on the pitch" (kickoff + 100 min) — editors can only
  // enter / finalise results after this point. Already-finished matches skip
  // the gate so mistakes can still be corrected.
  const canEditResult = isFinished || !!match.kickoff_finished;

  // Per-field provenance for the green ✓ / amber edit badge:
  //   "auto"      → value came from live-score-api and editor hasn't touched it
  //   "override"  → value was auto-fetched but editor changed it
  //   "manual"    → value was never auto-fetched (editor typed it from scratch)
  //   null        → input is empty, no badge
  const provenance = (key) => {
    const v = vals[key];
    if (v === "" || v == null) return null;
    const f = fetched ? fetched[key] : null;
    if (f == null) return "manual";
    return String(v) === String(f) ? "auto" : "override";
  };

  const setField = (k, v) => setVals((s) => ({ ...s, [k]: v }));

  const refetch = async () => {
    setRefetching(true);
    try {
      const { data } = await api.post(`/matches/${match.id}/fetch-stats`);
      if (data?.fetched_stats) {
        setVals((s) => ({
          ...s,
          ...Object.fromEntries(
            Object.entries(data.fetched_stats).filter(([, v]) => v != null && v !== "")
          ),
        }));
        toast.success("Stats refreshed from live-score-api · review and confirm");
        onChanged?.();
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRefetching(false);
    }
  };

  const remove = async () => {
    const label = `${match.home_team} vs ${match.away_team} on ${match.match_date}`;
    if (!window.confirm(
      `Delete ${label}? This will permanently remove the fixture and any predictions attached to it. Cannot be undone.`
    )) return;
    try {
      await api.delete(`/matches/${match.id}?hard=true`);
      toast.success(`${label} deleted`);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!canEditResult) {
      toast.error("This match hasn't finished yet — try again after the final whistle.");
      return;
    }
    const payload = {};
    for (const f of PREDICTION_FIELDS) {
      const n = parseInt(vals[f.key], 10);
      if (Number.isNaN(n) || n < 0) {
        toast.error(`Enter a valid number for ${f.label}`);
        return;
      }
      payload[f.key] = n;
    }
    // Explicit confirmation — this is the moment we finalize the result and
    // re-score every prediction. No dry-run / draft state, so make sure the
    // editor is deliberate.
    const label = `${match.home_team} vs ${match.away_team}`;
    const summary = PREDICTION_FIELDS.map((f) => `${f.label}: ${payload[f.key]}`).join(" · ");
    const verb = isFinished ? "update" : "finalise";
    if (!window.confirm(
      `Confirm the final result for ${label}?\n\n${summary}\n\n` +
      `This will ${verb} the result and immediately re-score every prediction and league table. ` +
      `You can still edit these numbers afterwards if a correction is needed.`
    )) return;
    setSaving(true);
    try {
      await api.post(`/matches/${match.id}/result`, payload);
      toast.success("Result saved · all predictions and league standings updated");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <article
      className="border border-zinc-200 rounded-sm"
      data-testid={`editor-match-${match.id}`}
    >
      <header className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
              {match.kickoff} · {match.match_date}
            </span>
            {isFinished ? (
              <Badge className="rounded-sm bg-zinc-950 text-white hover:bg-zinc-950 text-[10px] tracking-widest">FINISHED</Badge>
            ) : isAwaitingResult ? (
              <Badge
                data-testid={`editor-awaiting-result-${match.id}`}
                className="rounded-sm bg-amber-600 text-white hover:bg-amber-600 text-[10px] tracking-widest inline-flex items-center gap-1"
              >
                <AlertTriangle className="h-3 w-3" /> AWAITING RESULT
              </Badge>
            ) : (
              <Badge className="rounded-sm bg-[#0038FF] text-white hover:bg-[#0038FF] text-[10px] tracking-widest">SCHEDULED</Badge>
            )}
            {isAutoFetched && (
              <Badge
                data-testid={`editor-autofetched-${match.id}`}
                className="rounded-sm bg-emerald-600 text-white hover:bg-emerald-600 text-[10px] tracking-widest inline-flex items-center gap-1"
              >
                <Sparkles className="h-3 w-3" /> AUTO-FETCHED
              </Badge>
            )}
          </div>
          <h3 className="font-heading text-lg font-bold tracking-tight text-zinc-950 mt-0.5">
            {match.home_team} <span className="text-zinc-300">vs</span> {match.away_team}
          </h3>
          {isAutoFetched && (
            <p className="text-xs text-emerald-700 mt-1" data-testid={`editor-autofetched-hint-${match.id}`}>
              Powered by <strong>live-score-api</strong> {match.fetched_stats_at ? `· fetched ${new Date(match.fetched_stats_at).toLocaleString("en-GB")}` : ""}. Auto-filled values show a green ✓; anything you override turns amber. Click <strong>Set result</strong> to commit.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            onClick={refetch}
            disabled={refetching}
            variant="outline"
            data-testid={`editor-refetch-${match.id}`}
            className="h-9 rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refetching ? "animate-spin" : ""}`} />
            {refetching ? "Fetching…" : isAutoFetched ? "Re-fetch" : "Fetch stats"}
          </Button>
          <Button
            type="button"
            onClick={remove}
            disabled={refetching}
            variant="outline"
            data-testid={`editor-delete-match-${match.id}`}
            title="Delete this fixture (removes the match and its predictions permanently)"
            className="h-9 rounded-sm border-rose-300 text-rose-700 hover:bg-rose-50 text-xs"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            Delete
          </Button>
        </div>
      </header>
      <form onSubmit={save} className="p-5">
        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3 inline-flex items-center gap-1.5">
          <Trophy className="h-3.5 w-3.5" /> Actual full-time result
        </p>
        {!canEditResult && (
          <div
            data-testid={`editor-locked-until-ft-${match.id}`}
            className="mb-4 flex items-start gap-2 px-3 py-2 rounded-sm bg-zinc-50 border border-zinc-200"
          >
            <AlertTriangle className="h-4 w-4 text-zinc-500 mt-0.5 shrink-0" />
            <p className="text-xs text-zinc-700 leading-relaxed">
              <strong>Match hasn&apos;t finished yet.</strong> Results can only be entered {" "}
              <span className="font-semibold">100 minutes after kickoff</span> (90 min play +
              half-time + stoppage). The inputs will unlock once the final whistle window opens.
            </p>
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {PREDICTION_FIELDS.map((f) => {
            const p = provenance(f.key);
            const borderClass =
              p === "override"
                ? "border-amber-400 bg-amber-50/40 focus-visible:border-amber-500"
                : p === "auto"
                ? "border-emerald-300 bg-emerald-50/30 focus-visible:border-emerald-500"
                : "border-zinc-300";
            return (
              <div key={f.key}>
                <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 inline-flex items-center gap-1.5">
                  {f.label}
                  {p === "auto" && (
                    <span
                      data-testid={`editor-provenance-auto-${f.key}-${match.id}`}
                      title="Auto-filled from live-score-api"
                      className="inline-flex items-center text-emerald-600"
                    >
                      <CheckCircle2 className="h-3 w-3" aria-label="auto-fetched" />
                    </span>
                  )}
                  {p === "override" && (
                    <span
                      data-testid={`editor-provenance-override-${f.key}-${match.id}`}
                      title="You overrode the auto-fetched value"
                      className="inline-flex items-center text-amber-600"
                    >
                      <PencilLine className="h-3 w-3" aria-label="overridden" />
                    </span>
                  )}
                </Label>
                <Input
                  type="number"
                  min={0}
                  value={vals[f.key]}
                  onChange={(e) => setField(f.key, e.target.value)}
                  data-testid={`editor-actual-${f.key}-${match.id}`}
                  disabled={!canEditResult}
                  className={`mt-1.5 h-11 rounded-sm text-base font-semibold text-center ${borderClass} disabled:opacity-60 disabled:cursor-not-allowed`}
                />
              </div>
            );
          })}
        </div>
        <div className="mt-4">
          <Button
            type="submit"
            disabled={saving || !canEditResult}
            data-testid={`editor-save-result-${match.id}`}
            className="h-10 px-5 rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white font-semibold tracking-tight active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : !canEditResult ? "Locked until full-time" : isFinished ? "Update result" : "Set result (finalize)"}
          </Button>
        </div>
      </form>
    </article>
  );
}

/**
 * Editor-side counterpart to AdminPage's match-result list. Lets an editor pick
 * a date, see every fixture for that date, and enter / update the actual stats.
 * Saving hits the same backend endpoint (/matches/:id/result) and re-scores
 * every prediction the same way the admin flow does.
 */
export default function EditorResultsCard() {
  // Initialise the picker to the most recent matchday that actually has
  // fixtures in the active season, so the editor lands on the last day
  // with data instead of an empty "today" that hasn't happened yet. Falls
  // back to today (via the endpoint) when no past matchday exists.
  const [date, setDate] = useState(todayISO());
  const [dateInitialised, setDateInitialised] = useState(false);
  const [matches, setMatches] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);
  const [testFixtures, setTestFixtures] = useState(null); // null=not previewed, [] or [...] after preview

  // Resolve the "last matchday" once on mount. The editor can still
  // navigate the picker manually afterwards; we only auto-set the initial
  // value.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/matches/last-matchday");
        if (!cancelled && data?.date) setDate(data.date);
      } catch {
        // Silent — fall back to todayISO() default.
      } finally {
        if (!cancelled) setDateInitialised(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadPending = useCallback(async () => {
    try {
      const { data } = await api.get("/matches/pending-results");
      setPending(data || []);
    } catch (err) {
      // Non-fatal: don't toast on every load failure (editor may load it via dashboard)
      console.warn("Failed to load pending results:", formatApiError(err));
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/matches?date=${date}&exclude_test=true`);
      setMatches(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [date]);

  const reloadAll = useCallback(() => {
    load();
    loadPending();
  }, [load, loadPending]);

  useEffect(() => { if (dateInitialised) load(); }, [load, dateInitialised]);
  useEffect(() => { loadPending(); }, [loadPending]);

  const previewTestFixtures = async () => {
    try {
      const { data } = await api.get("/matches/test-fixtures");
      setTestFixtures(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const purgeTestFixtures = async () => {
    if (!window.confirm("Delete every test/bot-generated fixture from the database? This also removes their predictions. Cannot be undone.")) return;
    setPurging(true);
    try {
      const { data } = await api.delete("/matches/test-fixtures");
      toast.success(
        `Removed ${data.deleted_matches} test ${data.deleted_matches === 1 ? "fixture" : "fixtures"}` +
        (data.deleted_predictions ? ` + ${data.deleted_predictions} prediction${data.deleted_predictions === 1 ? "" : "s"}` : ""),
      );
      setTestFixtures([]);
      reloadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPurging(false);
    }
  };

  // Hide any pending match that is also showing in the selected-date list to
  // avoid rendering it twice.
  const dateIds = new Set(matches.map((m) => m.id));
  const pendingOnly = pending.filter((m) => !dateIds.has(m.id));

  return (
    <section className="border border-zinc-200 rounded-sm bg-white p-6" data-testid="editor-results-card">
      <header className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-1.5">
            <Trophy className="h-3.5 w-3.5" /> Match results
          </p>
          <div className="flex items-center gap-3 flex-wrap mt-1">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950">Enter match results.</h2>
            <LiveScoreStatusBadge />
          </div>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Drop in the actual full-time stats for any match. About 15 minutes after the final whistle, stats
            (including <strong>throw-ins</strong>) are auto-fetched from <strong>live-score-api</strong> and pre-filled
            with a green ✓ next to each field. Override any value (it&apos;ll turn amber), then saving instantly
            re-scores every prediction and refreshes the leagues.
          </p>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 inline-flex items-center gap-1.5">
            <CalendarDays className="h-3 w-3" /> View date
          </Label>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="editor-results-date-picker"
            className="mt-1 h-11 w-48 rounded-sm border-zinc-300"
          />
        </div>
      </header>

      {pendingOnly.length > 0 && (
        <div className="mb-6" data-testid="editor-pending-results">
          <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-sm bg-amber-50 border border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0" />
            <p className="text-sm text-amber-900">
              <strong>{pendingOnly.length}</strong> finished {pendingOnly.length === 1 ? "match" : "matches"} still awaiting your result.
              These stay here until you set the result.
            </p>
          </div>
          <div className="space-y-5">
            {pendingOnly.map((m) => <ResultRow key={`pending-${m.id}`} match={m} onChanged={reloadAll} />)}
          </div>
        </div>
      )}

      <div className="mb-6 border border-zinc-200 rounded-sm bg-zinc-50 p-4" data-testid="purge-test-fixtures-tool">
        <div className="flex items-start gap-3 flex-wrap">
          <Trash2 className="h-4 w-4 text-rose-600 mt-1 shrink-0" />
          <div className="flex-1 min-w-[260px]">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Cleanup</p>
            <p className="text-sm text-zinc-700 mt-1">
              Permanently delete every fixture created by automated tests or bot users (creator <code className="text-xs">@dugsy.app</code> / &quot;Test&quot; / &quot;QA&quot;, or matches whose only predictions are from bot accounts) along with their predictions.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={previewTestFixtures}
              disabled={purging}
              data-testid="preview-test-fixtures-btn"
              className="h-9 rounded-sm border-zinc-300 text-xs"
            >
              Preview
            </Button>
            <Button
              type="button"
              onClick={purgeTestFixtures}
              disabled={purging}
              data-testid="purge-test-fixtures-btn"
              className="h-9 rounded-sm bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> {purging ? "Deleting…" : "Delete test fixtures"}
            </Button>
          </div>
        </div>
        {testFixtures !== null && (
          <div className="mt-4 border-t border-zinc-200 pt-3" data-testid="test-fixtures-preview">
            {testFixtures.length === 0 ? (
              <p className="text-sm text-emerald-700">No test fixtures detected — your database is clean.</p>
            ) : (
              <>
                <p className="text-xs text-zinc-600 mb-2">
                  <strong>{testFixtures.length}</strong> fixture{testFixtures.length === 1 ? "" : "s"} will be removed:
                </p>
                <ul className="text-xs text-zinc-700 space-y-1 max-h-48 overflow-y-auto pr-2">
                  {testFixtures.map((m) => (
                    <li key={m.id} className="font-mono tabular-nums" data-testid={`test-fixture-row-${m.id}`}>
                      {m.match_date} {m.kickoff} · {m.home_team} vs {m.away_team}
                      {m.created_by_email ? <span className="text-zinc-400"> · by {m.created_by_email}</span> : null}
                      {m.prediction_count > 0 ? <span className="text-zinc-400"> · {m.prediction_count} pred{m.prediction_count === 1 ? "" : "s"}</span> : null}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </div>

      <div className="space-y-5">
        {loading ? (
          <p className="text-sm uppercase tracking-[0.18em] text-zinc-500">Loading…</p>
        ) : matches.length === 0 ? (
          <p
            data-testid="editor-results-empty"
            className="text-sm text-zinc-500 py-6 text-center border border-dashed border-zinc-300 rounded-sm"
          >
            No fixtures scheduled for this date.
          </p>
        ) : (
          matches.map((m) => <ResultRow key={m.id} match={m} onChanged={reloadAll} />)
        )}
      </div>
    </section>
  );
}
